import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/auth/AuthProvider'
import { fetchPlanPrices, PADDLE_PRICES } from '@/lib/paddle'
import { writePaddleTransaction } from '@/lib/firestore/profile'
import { Mascot } from '@/components/ui/Mascot'
import { track } from '@/lib/analytics'
import { httpsCallable } from 'firebase/functions'
import { sendEmailVerification } from 'firebase/auth'
import { auth, functions } from '@/lib/firebase'
import { takeReturnPath } from '@/lib/returnPath'

interface Plan {
  key: string
  name: string
  price: string
  cadence: string
  highlight?: string
  best?: boolean
}

const PLANS: Plan[] = [
  { key: '1month', name: 'Monthly', price: '$14.99', cadence: 'per month' },
  { key: '3month', name: 'Quarterly', price: '$29.99', cadence: 'every 3 months' },
  { key: '1year', name: 'Yearly', price: '$59.99', cadence: 'per year', highlight: '7-day free trial', best: true },
]

export function Gate() {
  const { t } = useTranslation()
  const { user, access, recheckAccess, signOut, profileMissing } = useAuth()
  const navigate = useNavigate()
  const [selected, setSelected] = useState('1year')
  const [notice, setNotice] = useState('')
  // A web purchase is parked for this email but the account hasn't proven it owns it.
  const [verifyPending, setVerifyPending] = useState(false)
  const [verifyBusy, setVerifyBusy] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [livePrices, setLivePrices] = useState<Record<string, string> | null>(null)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const purchasedRef = useRef(false)

  // Replace the fallback price strings with Paddle's live localized prices.
  useEffect(() => {
    let cancelled = false
    void fetchPlanPrices().then((prices) => {
      if (!cancelled && prices) setLivePrices(prices)
    })
    return () => {
      cancelled = true
    }
  }, [])

  // Funnel analytics — same scheme as mobile (paywall_viewed → paywall_converted
  // keyed on `source`), so GA4 conversion reports cover web with source=web_gate.
  const viewedRef = useRef(false)
  useEffect(() => {
    if (viewedRef.current) return
    if (profileMissing) {
      viewedRef.current = true
      track('web_no_account_shown', { provider: 'social_or_email' })
    } else if (access === 'denied') {
      viewedRef.current = true
      track('paywall_viewed', { source: 'web_gate' })
    }
  }, [profileMissing, access])

  // Leave the gate as soon as access is granted (after subscribing or a verify).
  useEffect(() => {
    if (access === 'granted') {
      if (purchasedRef.current) {
        track('paywall_converted', { source: 'web_gate' })
        purchasedRef.current = false
      }
      // 4.40 RS: back to the shared recipe that sent a free user here (share paths only).
      navigate(takeReturnPath() ?? '/diary', { replace: true })
    }
  }, [access, navigate])

  // Returned from the funnel-domain checkout. Capture the params once —
  // the activation write needs `user`, which may still be restoring.
  const [pendingTxn, setPendingTxn] = useState<{ txn: string } | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('purchased') !== '1') return
    window.history.replaceState({}, '', window.location.pathname)
    purchasedRef.current = true
    track('checkout_returned', { source: 'web_gate' })
    setProcessing(true)
    setPendingTxn({ txn: params.get('txn') || '' })
  }, [])

  // Activation: write the Paddle transaction id to kpal_users — this fires the
  // transferPaddleSubscription trigger (RevenueCat receipt + isPremium), the
  // SAME chain funnel purchases use — then poll until access lands.
  useEffect(() => {
    if (!pendingTxn || !user) return
    // No kpal_users doc: never write (a stub would block mobile profile
    // creation). The Paddle webhook activates server-side instead.
    if (profileMissing) return
    let cancelled = false
    void (async () => {
      if (pendingTxn.txn) {
        try {
          await writePaddleTransaction(user.uid, pendingTxn.txn, user.email)
        } catch (err) {
          // updateDoc throws 'not-found' when the profile doc doesn't exist;
          // the webhook path can still activate, so the poll continues regardless.
          console.warn('writePaddleTransaction failed (webhook will activate):', err)
        }
      }
      let tries = 0
      const poll = async () => {
        if (cancelled) return
        tries += 1
        await recheckAccess()
        if (cancelled) return
        if (tries < 20) pollRef.current = setTimeout(() => void poll(), 3000)
        else {
          setProcessing(false)
          setNotice(t('webapp.paywall.activationSlow', 'Payment received — activation is taking a little longer than usual. This page will unlock automatically; you can also click “Already subscribed? Refresh”.'))
        }
      }
      void poll()
    })()
    return () => {
      cancelled = true
      if (pollRef.current) clearTimeout(pollRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when txn+user are ready
  }, [pendingTxn, user, profileMissing])


  // A purchase may be parked server-side for this email (funnel write failed,
  // signed up after paying). Claim it — or, for an unverified email+password
  // account, ask the user to verify so it can be claimed. Once per gate visit.
  const claimTriedRef = useRef(false)
  async function claimWebPurchase(): Promise<'claimed' | 'pending_unverified' | 'none'> {
    try {
      const res = await httpsCallable<unknown, { status?: 'claimed' | 'pending_unverified' | 'none' }>(functions, 'claimWebPurchase')({})
      return res.data?.status ?? 'none'
    } catch {
      return 'none'
    }
  }
  useEffect(() => {
    if (!user || access !== 'denied' || pendingTxn || claimTriedRef.current) return
    claimTriedRef.current = true
    void (async () => {
      // Back from the verification email's "Continue" button: refresh the
      // Auth user so emailVerified is current before asking the server.
      const cameBackVerified = new URLSearchParams(window.location.search).get('verified') === '1'
      if (cameBackVerified) {
        window.history.replaceState({}, '', window.location.pathname)
        try { await auth.currentUser?.reload() } catch { /* claim below still answers correctly */ }
      }
      const status = await claimWebPurchase()
      if (status === 'claimed') await recheckAccess()
      else if (status === 'pending_unverified') setVerifyPending(true)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, access, pendingTxn])

  async function onSendVerification() {
    if (!auth.currentUser) return
    setVerifyBusy(true)
    try {
      await sendEmailVerification(auth.currentUser, {
        url: `${window.location.origin}/upgrade?verified=1`,
        handleCodeInApp: false,
      })
      setNotice(t('webPurchase.sentBody', 'Open the link in the email, then come back here and tap "I\u2019ve verified".'))
    } catch {
      setNotice(t('webPurchase.sendFailed', 'Couldn\u2019t send the email. Please try again later.'))
    } finally {
      setVerifyBusy(false)
    }
  }

  async function onVerified() {
    if (!auth.currentUser) return
    setVerifyBusy(true)
    try {
      await auth.currentUser.reload()
      const status = await claimWebPurchase()
      if (status === 'claimed') {
        setVerifyPending(false)
        await recheckAccess()
      } else if (status === 'pending_unverified') {
        setNotice(t('webPurchase.stillUnverified', 'That address isn\u2019t verified yet \u2014 open the link in the email first.'))
      } else {
        setVerifyPending(false)
        await recheckAccess()
      }
    } finally {
      setVerifyBusy(false)
    }
  }

  async function onSubscribe() {
    // Paddle checkout must run on an approved domain. start.kidneypal.app (the
    // funnel) is approved, so hand off to its standalone /web-checkout page
    // with the plan + signed-in email; it returns here with ?purchased=1.
    track('begin_checkout', { source: 'web_gate', plan: selected })
    const params = new URLSearchParams({ price: PADDLE_PRICES[selected] })
    if (user?.email) params.set('email', user.email)
    window.location.href = `https://start.kidneypal.app/web-checkout.html?${params.toString()}`
  }

  async function onRefresh() {
    setRefreshing(true)
    setNotice('')
    await recheckAccess()
    setRefreshing(false)
  }

  // Never render the paywall while the access decision is still resolving —
  // a premium user on a cold load must not see plan cards or a Subscribe
  // button (double-purchase exposure) for even a frame.
  if (access === 'checking' && !processing) {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary" />
      </div>
    )
  }

  // No KidneyPal account behind this sign-in: block the paywall entirely —
  // account creation + onboarding happen in the mobile app (or the funnel).
  if (profileMissing) {
    return (
      <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
        <div className="w-full max-w-md animate-fade-in rounded-xl border border-border bg-card p-xl text-center shadow-lg">
          <div className="mb-md flex justify-center">
            <Mascot pose="shrugging" size={96} animation="float" />
          </div>
          <h1 className="text-h3 font-bold text-text">KidneyPal</h1>
          <p className="mt-sm text-body text-text-secondary">
            {t('webapp.authNoSignup', 'New to KidneyPal? Create your account in the mobile app, then sign in here.')}
          </p>
          <p className="mt-sm text-caption text-text-muted">
            {t('webapp.authSwitchHint', 'Already have an account? Sign out and sign in with the method you used before.')}
          </p>
          <div className="mt-lg flex items-center justify-between text-caption text-text-muted">
            <span className="truncate" title={user?.email ?? ''}>
              {t('webapp.paywall.signedInAs', 'Signed in as')} {user?.email}
            </span>
            <button onClick={() => void signOut()} className="text-text-secondary hover:underline">
              {t('settings.signOut', 'Sign Out')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-full w-full items-center justify-center bg-bg p-md">
      <div className="w-full max-w-2xl animate-fade-in rounded-xl border border-border bg-card p-xl shadow-lg">
        <div className="mb-lg text-center">
          <div className="mb-md flex justify-center">
            <Mascot pose="pointingright" size={96} animation="float" />
          </div>
          <h1 className="text-h2 font-bold text-text">
            {t('webapp.paywall.title', 'KidneyPal on the web is a Pro feature')}
          </h1>
          <p className="mx-auto mt-sm max-w-md text-body text-text-secondary">
            {t(
              'webapp.paywall.subtitle',
              'Subscribe to use KidneyPal from any computer — your diary, scans, recipes, and reports, in sync with your phone.',
            )}
          </p>
        </div>

        {processing ? (
          <div className="flex flex-col items-center gap-md py-lg">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-border border-t-primary" />
            <p className="text-body text-text-secondary">
              {t('webapp.paywall.activating', 'Activating your subscription…')}
            </p>
          </div>
        ) : (
          <>
            <div className="mb-lg grid gap-md sm:grid-cols-3">
              {PLANS.map((plan) => (
                <button
                  key={plan.key}
                  onClick={() => setSelected(plan.key)}
                  className={
                    'relative flex flex-col items-center rounded-xl border-2 p-md text-center transition-colors ' +
                    (selected === plan.key ? 'border-primary bg-bg-secondary' : 'border-border hover:border-primary')
                  }
                >
                  {plan.best ? (
                    <span className="absolute -top-3 rounded-full bg-primary px-sm py-xs text-caption font-semibold text-white">
                      {t('webapp.paywall.bestValue', 'Best value')}
                    </span>
                  ) : null}
                  <span className="text-body font-semibold text-text">
                    {t(`paywall.plans.${plan.key}.name`, plan.name)}
                  </span>
                  <span className="mt-xs text-h3 font-bold text-text">{livePrices?.[plan.key] ?? plan.price}</span>
                  <span className="text-caption text-text-muted">
                    {t(`paywall.plans.${plan.key}.cadence`, plan.cadence)}
                  </span>
                  {plan.highlight ? (
                    <span className="mt-xs text-caption font-medium text-safe">
                      {t(`paywall.plans.${plan.key}.highlight`, plan.highlight)}
                    </span>
                  ) : null}
                </button>
              ))}
            </div>

            {verifyPending ? (
              <div className="mb-md rounded-xl border border-primary/40 bg-primary/5 p-md text-center">
                <p className="text-body-sm text-text">
                  {t('webPurchase.verifyNotice', { email: user?.email ?? '', defaultValue: 'We found a KidneyPal Pro purchase for {{email}}. Verify this email address to activate it.' })}
                </p>
                <div className="mt-sm flex flex-wrap justify-center gap-sm">
                  <button onClick={() => void onSendVerification()} disabled={verifyBusy} className="rounded-full bg-primary px-md py-sm text-body-sm font-semibold text-white disabled:opacity-60">
                    {t('webPurchase.sendEmail', 'Send verification email')}
                  </button>
                  <button onClick={() => void onVerified()} disabled={verifyBusy} className="rounded-full border border-border px-md py-sm text-body-sm font-medium text-text-secondary disabled:opacity-60">
                    {t('webPurchase.verified', 'I\u2019ve verified \u2014 activate')}
                  </button>
                </div>
              </div>
            ) : null}
            {notice ? <p className="mb-md text-center text-body-sm text-primary">{notice}</p> : null}

            <button
              onClick={() => void onSubscribe()}
              className="w-full rounded-lg bg-primary px-md py-md text-button font-semibold text-white transition-opacity hover:opacity-90"
            >
              {t('webapp.paywall.subscribe', 'Subscribe')}
            </button>

            <button
              onClick={() => void onRefresh()}
              disabled={refreshing}
              className="mt-md w-full text-center text-body-sm text-text-secondary hover:text-primary disabled:opacity-60"
            >
              {refreshing
                ? t('webapp.paywall.checking', 'Checking…')
                : t('webapp.paywall.alreadySubscribed', 'Already subscribed? Refresh')}
            </button>
          </>
        )}

        <div className="mt-lg flex items-center justify-between text-caption text-text-muted">
          <span className="truncate" title={user?.email ?? ''}>
            {t('webapp.paywall.signedInAs', 'Signed in as')} {user?.email}
          </span>
          <button onClick={() => void signOut()} className="text-text-secondary hover:underline">
            {t('settings.signOut', 'Sign Out')}
          </button>
        </div>
      </div>
    </div>
  )
}
